// Holds the logged-in user's Google access token for the lifetime of the browser tab.
//
// It used to live in a `gtoken` cookie, which any script on the page could read and exfiltrate
// for offline use until the token expired, and which rode along on every request to the origin
// whether the app wanted it there or not. It then moved to a module variable, which fixed both
// of those and introduced a different problem: a page reload dropped it, so the Agent
// Monitoring page asked the user to reconnect after every refresh, new tab, or crash.
//
// It now lives in sessionStorage, which keeps what mattered about the move away from cookies:
//
//   - Never sent automatically. Unlike a cookie it does not attach to requests, so it cannot
//     ride along on a request the app did not make.
//   - Dies with the tab. sessionStorage is per-tab and cleared on close, so it is not left
//     behind on a shared machine the way localStorage would be.
//   - Same-origin only.
//
// What it gives up versus a module variable is that injected script can read it directly. That
// is a smaller loss than it sounds: script running on the page can already read a module
// variable by hooking the module, so this trades a marginal difference against the reload
// problem, which is the one users actually hit.
//
// The expiry is stored alongside and re-checked on read, so a token restored from a previous
// page load is never handed out past its lifetime. Google issues these for about an hour;
// nothing here can extend that, and a longer session needs the auth-code flow with a refresh
// token held server-side.

const STORAGE_KEY = 'bbvr.googleAccessToken';
const SESSION_KEY = 'bbvr.agentSessionToken';

const listeners = new Set();

function notify() {
    for (const listener of listeners) {
        listener();
    }
}

// Every access is wrapped: sessionStorage throws in Safari private mode and when storage is
// disabled by policy, and an auth helper must not be the thing that takes the page down.
function readStored() {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.accessToken !== 'string' || typeof parsed?.expiresAtMs !== 'number') {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function writeStored(value) {
    try {
        if (value === null) {
            window.sessionStorage.removeItem(STORAGE_KEY);
        } else {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
        }
    } catch {
        // Storage unavailable. The in-memory fallback below still works for this page load,
        // so the user is no worse off than before this change.
    }
}

let memoryFallback = null;

export function setGoogleAccessToken(token, expiresInSeconds) {
    const accessToken = typeof token === 'string' && token.length > 0 ? token : null;
    if (accessToken === null) {
        clearGoogleAccessToken();
        return;
    }
    // Expire a minute early so a request that is in flight when the token lapses is not sent
    // with a token the API will already have rejected.
    const lifetime = typeof expiresInSeconds === 'number' && expiresInSeconds > 60 ? expiresInSeconds - 60 : 0;
    const value = { accessToken, expiresAtMs: Date.now() + lifetime * 1000 };
    memoryFallback = value;
    writeStored(value);
    notify();
}

export function clearGoogleAccessToken() {
    memoryFallback = null;
    writeStored(null);
    notify();
}

export function getGoogleAccessToken() {
    const stored = readStored() ?? memoryFallback;
    if (stored === null) {
        return null;
    }
    if (Date.now() >= stored.expiresAtMs) {
        // Drop it rather than sending a token we know is stale.
        clearGoogleAccessToken();
        return null;
    }
    return stored.accessToken;
}

// The agent's own session token, traded for a verified Google token once at login.
//
// Google's access token lasts about an hour and cannot be extended from the browser, so
// depending on it directly meant re-prompting hourly. This one is issued by the agent after it
// verifies the Google token, lasts four hours, and is what every later request presents.
// Stored the same way and for the same reasons as the Google token above.

let agentSessionFallback = null;

// Wrapped for the same reason as the Google token's helpers: sessionStorage throws in Safari
// private mode and under enterprise policy, and an auth helper must not take the page down.
//
// Deliberately no notify() here. That listener set belongs to the Google token
// (subscribeToGoogleAccessToken); firing it for a session change would tell every future
// subscriber the wrong thing happened.
function readSession() {
    try {
        const raw = window.sessionStorage.getItem(SESSION_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.token !== 'string' || typeof parsed?.expiresAtMs !== 'number') {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function writeSession(value) {
    try {
        if (value === null) {
            window.sessionStorage.removeItem(SESSION_KEY);
        } else {
            window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(value));
        }
    } catch {
        // Storage unavailable. The in-memory copy still works for this page load.
    }
}

export function setAgentSessionToken(token, expiresAtMs) {
    if (typeof token !== 'string' || token.length === 0 || typeof expiresAtMs !== 'number') {
        clearAgentSessionToken();
        return;
    }
    agentSessionFallback = { token, expiresAtMs };
    writeSession({ token, expiresAtMs });
}

export function clearAgentSessionToken() {
    agentSessionFallback = null;
    writeSession(null);
}

export function getAgentSessionToken() {
    const stored = readSession() ?? agentSessionFallback;
    if (stored === null) {
        return null;
    }
    // A minute of headroom, same reasoning as the Google token: do not send one we know the
    // API is about to reject.
    if (Date.now() >= stored.expiresAtMs - 60 * 1000) {
        clearAgentSessionToken();
        return null;
    }
    return stored.token;
}

export function subscribeToGoogleAccessToken(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
